import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Flex, IconButton, Popover, RadioGroup, Text, TextField } from '@radix-ui/themes';
import { Funnel, Search } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
export const FILTERABLE_CHAIN_NAMES = [
    'ETH',
    'TRON',
    'STARKNET',
    'SOLANA',
    'BTC',
    'LTC',
    'DOGE',
    'BCH',
    'TON',
    'SUI',
    'XRPL',
];
export function WalletsChainSelect(props) {
    const { t } = useTranslation();
    const [searchValue, setSearchValue] = useState('');
    /*
     * The connect modal renders inside a scroll-locked Radix Dialog (the app's
     * modal stack). A Popover portalled to <body> falls outside that lock, so the
     * wheel is blocked over it. Portal the Popover into the nearest dialog ancestor
     * instead, keeping it inside the lock's allowed subtree so it scrolls normally.
     */
    const triggerRef = useRef(null);
    const [portalContainer, setPortalContainer] = useState(null);
    const { isOpen, setIsOpen, selectedChain, handleSelectedChainChange, shouldExcludeWallet, allBlockchains, isMobile, renderModal, } = props;
    // List of blockchain items displayed in the chain select
    const filterableChains = useMemo(() => {
        return allBlockchains.filter((blockchain) => {
            // Drop blockchain if its name does not contain the search value
            if (!blockchain.name.toLowerCase().includes(searchValue.toLowerCase())) {
                return false;
            }
            // Drop blockchain if it is not in the FILTERABLE_CHAIN_NAMES
            if (!FILTERABLE_CHAIN_NAMES.includes(blockchain.name)) {
                return false;
            }
            // Drop blockchain if its related wallets are excluded
            if (!!shouldExcludeWallet && shouldExcludeWallet({ supportedChains: [blockchain] })) {
                return false;
            }
            return true;
        });
    }, [allBlockchains, searchValue]);
    const selectedBlockchainItem = useMemo(() => !!selectedChain
        ? filterableChains.find((blockchain) => blockchain.name === selectedChain)
        : null, [selectedChain]);
    function handleClose() {
        setSearchValue('');
        setIsOpen(false);
    }
    function handleChainSelect(blockchainName) {
        handleSelectedChainChange(blockchainName);
        handleClose();
    }
    /*
     * The filter opens a chain picker: a Popover on desktop (Popover.Trigger owns
     * the click) and the render-prop dialog on mobile (needs its own onClick).
     * A selected chain tints the funnel with the accent color to signal an active filter.
     */
    function renderTrigger(onClick, ref) {
        return (_jsx(IconButton, { ref: ref, id: "app-connect-wallet-modal-chain-select-btn", type: "button", size: "3", variant: "ghost", color: selectedBlockchainItem ? undefined : 'gray', "aria-label": t('Filter by blockchain'), onClick: onClick, children: _jsx(Funnel, { size: 18, "aria-hidden": true }) }));
    }
    const searchField = (_jsx(TextField.Root, { id: "app-connect-wallet-modal-search-chain-input", value: searchValue, onChange: (event) => setSearchValue(event.target.value), placeholder: t('Search Blockchain'), children: _jsx(TextField.Slot, { children: _jsx(Search, { size: 16, "aria-hidden": true }) }) }));
    const optionsList = (_jsx(RadioGroup.Root, { value: selectedChain ?? 'all', onValueChange: (value) => handleChainSelect(value === 'all' ? null : value), children: _jsxs(Flex, { direction: "column", children: [_jsx(Text, { as: "label", size: "2", children: _jsxs(Flex, { align: "center", justify: "between", p: "2", className: "chain-select-option", children: [_jsx(Text, { children: t('All') }), _jsx(RadioGroup.Item, { value: "all" })] }) }), filterableChains.map((blockchain) => (_jsx(Text, { as: "label", size: "2", children: _jsxs(Flex, { align: "center", justify: "between", p: "2", className: "chain-select-option", children: [_jsxs(Flex, { align: "center", gap: "2", minWidth: "0", children: [_jsx("img", { src: blockchain.logo, alt: blockchain.name, style: { width: '16px', height: '16px', objectFit: 'contain', flexShrink: 0 } }), _jsx(Text, { truncate: true, children: blockchain.displayName })] }), _jsx(RadioGroup.Item, { value: blockchain.name })] }) }, blockchain.name)))] }) }));
    if (isMobile) {
        return (_jsxs(_Fragment, { children: [renderTrigger(() => setIsOpen(true)), renderModal({
                    open: isOpen,
                    onClose: handleClose,
                    title: t('Filter by blockchain'),
                    children: (_jsxs(Flex, { direction: "column", gap: "3", children: [searchField, optionsList] })),
                })] }));
    }
    return (_jsxs(Popover.Root, { open: isOpen, onOpenChange: (nextOpen) => {
            if (!nextOpen) {
                handleClose();
            }
            else {
                setPortalContainer(triggerRef.current?.closest('[role="dialog"]') ?? null);
                setIsOpen(true);
            }
        }, children: [_jsx(Popover.Trigger, { children: renderTrigger(undefined, triggerRef) }), _jsx(Popover.Content, { width: "260px", align: "end", container: portalContainer ?? undefined, children: _jsxs(Flex, { direction: "column", gap: "3", children: [_jsx(Text, { align: "left", size: "2", weight: "medium", children: t('Filter by blockchain') }), searchField, _jsx(Box, { maxHeight: "260px", overflowY: "auto", children: optionsList })] }) })] }));
}
